import dayjs from "dayjs";

/**
 * Adjusts the given date string by a specified number of minutes.
 * @param dateString - The original date string in UTC.
 * @param minutes - The number of minutes to adjust the date by.
 * @returns The adjusted date formatted as 'yyyy-MM-dd'T'HH:mm:ss.SSSSSSS' or an error message if the date is invalid.
 */
export function adjustDateByMinutes(
  dateString: string | undefined,
  minutes: number
): string | undefined {
  if (dateString === undefined) return undefined;

  try {
    const date = dayjs(dateString);
    
    if (!date.isValid()) {
      return dateString;
      //throw new Error('Invalid date string');
    }
    
    const adjustedDate = date.add(minutes, 'minute');
    return adjustedDate.format('YYYY-MM-DDTHH:mm:ss.SSSSSSS');
  } catch (error) {
    return dateString;
  }
}

export function convertDateExpressionToDate(dateExpression: string): Date {
  switch (dateExpression) {
    case "Today":
      return dayjs().startOf("day").toDate();
    case "Tomorrow":
      return dayjs().add(1, 'day').startOf("day").toDate();
    case "Yesterday":
      return dayjs().add(-1, 'day').startOf("day").toDate();
    case "FirstOfCurrentMonth":
      return dayjs().startOf("month").toDate();
    case "FirstOfNextMonth":
      return dayjs().add(1, 'month').startOf("month").toDate();
    case "FirstOfLastMonth":
      return dayjs().add(-1, 'month').startOf("month").toDate();
    case "LastOfCurrentMonth":
      return dayjs().endOf("month").toDate();
    case "LastOfNextMonth":
      return dayjs().add(1, 'month').endOf("month").toDate();
    case "LastOfLastMonth":
      return dayjs().add(-1, 'month').endOf("month").toDate();
    case "FirstOfCurrentYear":
      return dayjs().startOf("year").toDate();
    case "FirstOfNextYear":
      return dayjs().add(1, 'year').startOf("year").toDate();
    case "FirstOfLastYear":
      return dayjs().add(-1, 'year').startOf("year").toDate();  
    default:
      throw new Error(`'${dateExpression}' is not a valid date expression`);
  }
}

/** House date formats. A column overrides these with meta_column.date_format. */
export const DEFAULT_DATE_FORMAT = "DD-MMM-YYYY";
export const DEFAULT_DATETIME_FORMAT = "DD-MMM-YYYY HH:mm";
export const DEFAULT_TIME_FORMAT = "HH:mm";

/**
 * Picks the display format for a column from its database type, so a date shows a date
 * and a timestamp shows the time with it.
 *
 * control_type cannot make this distinction - it collapses every date and time type onto
 * "date" - and the framework runs on more than one database, so the type names differ:
 *
 *   PostgreSQL  date, timestamp without time zone, timestamp with time zone, time...
 *   SQL Server  date, datetime, datetime2, smalldatetime, datetimeoffset, time
 *   MySQL       date, datetime, timestamp, time
 *   Oracle      DATE, TIMESTAMP(6), TIMESTAMP WITH TIME ZONE
 *
 * Matching on what they have in common - "timestamp" or "datetime" anywhere in the name -
 * covers all of them without a per-provider table. Order matters: datetimeoffset and
 * smalldatetime must be caught before the bare "time" test.
 *
 * @param dataType the column's database type, any provider's spelling
 * @param dateFormat meta_column.date_format, which wins when set
 */
export function columnDateFormat(
  dataType?: string | null,
  dateFormat?: string | null
): string {
  if (dateFormat) return dateFormat;

  const t = (dataType ?? "").toLowerCase();
  if (t.includes("timestamp") || t.includes("datetime"))
    return DEFAULT_DATETIME_FORMAT;
  if (t.startsWith("time")) return DEFAULT_TIME_FORMAT;
  return DEFAULT_DATE_FORMAT;
}
